<script setup lang="ts">
import { Search, UserPlus, X, Check, MessageSquarePlus } from '@lucide/vue'
import { useDebounceFn } from '@vueuse/core'
import { ref, watch } from 'vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { apiFetch } from '@ja/portal-query'

import { useCommunicationTranslations } from '../composables/useCommunicationTranslations'
import { useMessenger } from '../composables/useMessenger'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const messenger = useMessenger()
const { tm } = useCommunicationTranslations('communication')
const searchQuery = ref('')
const users = ref<any[]>([])
const selectedUsers = ref<any[]>([])
const customTitle = ref('')
const isLoading = ref(false)

const searchUsers = useDebounceFn(async () => {
  if (!searchQuery.value.trim()) {
    users.value = []
    return
  }

  isLoading.value = true
  try {
    const response = await apiFetch<{ data: any[] }>(`/api/recipients/search-contacts?q=${encodeURIComponent(searchQuery.value)}`)
    users.value = response.data
  } catch (error) {
    console.error('Failed to search users:', error)
  } finally {
    isLoading.value = false
  }
}, 300)

watch(searchQuery, searchUsers)

const toggleUserSelection = (user: any) => {
  const index = selectedUsers.value.findIndex(u => u.id === user.id)
  if (index === -1) {
    selectedUsers.value.push(user)
    searchQuery.value = ''
    users.value = []
  } else {
    selectedUsers.value.splice(index, 1)
  }
}

const isSelected = (userId: string) => {
  return selectedUsers.value.some(u => u.id === userId)
}

const startChat = async () => {
  if (selectedUsers.value.length === 0) return
  
  isLoading.value = true
  try {
    const userIds = selectedUsers.value.map(u => u.id)
    await messenger.openDirectChat(userIds, customTitle.value || undefined)
    emit('update:open', false)
    reset()
  } catch (error) {
    console.error('Failed to start chat:', error)
  } finally {
    isLoading.value = false
  }
}

const reset = () => {
  searchQuery.value = ''
  selectedUsers.value = []
  customTitle.value = ''
  users.value = []
}

watch(() => props.open, (newVal) => {
  if (!newVal) reset()
})
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="flex flex-col sm:max-w-[425px] h-[600px]">
      <DialogHeader>
        <DialogTitle>{{ tm('conversations.new_conversation') }}</DialogTitle>
      </DialogHeader>
      
      <div class="flex-1 overflow-y-auto space-y-4 py-4">
        <!-- Selected Users Tags -->
        <div v-if="selectedUsers.length > 0" class="flex flex-wrap gap-2 rounded-md border p-2 bg-muted/30">
          <Badge
            v-for="user in selectedUsers"
            :key="user.id"
            variant="secondary"
            class="flex items-center gap-1 py-1"
          >
            {{ user.name }}
            <X class="size-3 cursor-pointer hover:text-destructive" @click="toggleUserSelection(user)" />
          </Badge>
        </div>

        <!-- Custom Title -->
        <div class="space-y-2">
          <label class="text-xs font-medium text-muted-foreground px-1">{{ tm('conversations.title_optional') }}</label>
          <Input
            v-model="customTitle"
            :placeholder="tm('conversations.title_placeholder')"
            class="h-9"
          />
        </div>

        <!-- Search Input -->
        <div class="space-y-2">
          <label class="text-xs font-medium text-muted-foreground px-1">{{ tm('conversations.participants') }}</label>
          <div class="relative">
            <Search class="absolute left-2 top-2.5 size-4 text-muted-foreground" />
            <Input
              v-model="searchQuery"
              :placeholder="tm('conversations.search_people')"
              class="pl-8 h-9"
              auto-focus
            />
          </div>
        </div>

        <!-- Search Results -->
        <div class="space-y-1">
          <div v-if="isLoading" class="py-4 text-center text-sm text-muted-foreground">
            {{ tm('conversations.searching') }}
          </div>
          <div v-else-if="searchQuery && users.length === 0" class="py-4 text-center text-sm text-muted-foreground">
            {{ tm('conversations.no_people_found') }}
          </div>
          
          <button
            v-for="user in users"
            :key="user.id"
            class="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-muted"
            :class="{ 'opacity-50 cursor-not-allowed': isSelected(user.id) }"
            :disabled="isSelected(user.id)"
            @click="toggleUserSelection(user)"
          >
            <div class="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
              <span class="text-primary text-xs font-bold">{{ user.name.charAt(0).toUpperCase() }}</span>
            </div>
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium">{{ user.name }}</p>
              <p class="truncate text-xs text-muted-foreground">{{ user.email }}</p>
            </div>
            <Check v-if="isSelected(user.id)" class="size-4 text-primary" />
            <UserPlus v-else class="size-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      <DialogFooter class="border-t pt-4">
        <Button
          class="w-full"
          :disabled="selectedUsers.length === 0 || isLoading"
          @click="startChat"
        >
          <MessageSquarePlus class="mr-2 size-4" />
          {{ tm('conversations.create_chat') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
